import { FeatureFlags } from "@/modules/feature-flags/consts/FeatureFlags";
import {
  awaitFeatureFlagsLoaded,
  isFeatureEnabled,
} from "@/modules/feature-flags/utils/posthog";

import { getMode, MODE } from "./env-mode";
import { enablePostHogSessionReplay } from "./posthog";
import { disableSentrySessionReplay } from "./sentry";

/**
 * Decide the session replay vendor for this page load.
 *
 * Sentry Session Replay is armed eagerly during `initSentry`, so it is already
 * running by the time this runs. The orchestrator only acts when the PostHog
 * flag is on: stop Sentry, then start PostHog. When the flag is off (default),
 * this is a no-op and Sentry keeps recording.
 *
 * Sequencing matters — Sentry is stopped before PostHog starts so the two
 * recorders never run in parallel once the swap completes.
 *
 * No-op in development and test modes — neither Sentry nor PostHog init runs
 * in those environments.
 */
export async function initSessionReplay(): Promise<void> {
  const mode = getMode();
  if (mode === MODE.DEVELOPMENT || mode === MODE.TEST) {
    return;
  }

  await awaitFeatureFlagsLoaded();

  const usePostHogReplay = isFeatureEnabled(
    FeatureFlags.PostHogSessionReplay,
    false,
  );

  if (!usePostHogReplay) {
    return;
  }

  // Stop Sentry first: if it rejects, PostHog never starts and we stay on
  // Sentry alone. Reordering would risk both recorders running on a failed stop.
  await disableSentrySessionReplay();
  enablePostHogSessionReplay();
}
